import { useState, useEffect, useCallback } from 'react';
import type { IbkrConnectionInput, IbkrConnectionResponse } from '@covered-calls/shared';
import { ConnectionStatus } from '@covered-calls/shared';
import { ApiClientError } from '../services/api-client';
import {
  getIbkrConnection,
  saveIbkrConnection,
  deleteIbkrConnection,
  testIbkrConnection,
} from '../services/ibkr-settings-service';
import { useIbkrLiveStatus, type LiveStatusUiState } from '../hooks/useIbkrLiveStatus';

/**
 * IBKR Connection Settings component.
 * Configures the ngrok gateway URL for automated market data fetching.
 */

const STATUS_COLORS: Record<LiveStatusUiState, string> = {
  [ConnectionStatus.CONNECTED]: 'var(--color-success)',
  [ConnectionStatus.DISCONNECTED]: 'var(--color-muted)',
  [ConnectionStatus.AUTH_FAILED]: 'var(--color-danger)',
  CHECKING: 'var(--color-muted)',
  UNKNOWN: 'var(--color-muted)',
};

const STATUS_LABELS: Record<LiveStatusUiState, string> = {
  [ConnectionStatus.CONNECTED]: 'Connected',
  [ConnectionStatus.DISCONNECTED]: 'Disconnected',
  [ConnectionStatus.AUTH_FAILED]: 'Authentication Failed',
  CHECKING: 'Checking…',
  UNKNOWN: 'Unknown',
};

export function IbkrConnectionSettings() {
  const [connection, setConnection] = useState<IbkrConnectionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [gatewayUrl, setGatewayUrl] = useState('');
  const [enabled, setEnabled] = useState(false);

  // Live connection status (independent of the stored config and the enabled toggle).
  const { status, checkedAt, isChecking, message, refresh } = useIbkrLiveStatus();

  const isDirty = connection
    ? (gatewayUrl !== (connection.gatewayUrl ?? '') || enabled !== connection.enabled)
    : gatewayUrl.trim() !== '';

  const fetchConnection = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const data = await getIbkrConnection();
      setConnection(data);
      if (data) {
        setGatewayUrl(data.gatewayUrl ?? '');
        setEnabled(data.enabled);
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(`Failed to load IBKR settings: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  async function handleSave() {
    setSuccessMessage('');
    setErrorMessage('');

    if (!gatewayUrl.trim()) {
      setErrorMessage('Gateway URL is required.');
      return;
    }

    const input: IbkrConnectionInput = {
      gatewayUrl: gatewayUrl.trim(),
      enabled,
    };

    setIsSaving(true);
    try {
      const result = await saveIbkrConnection(input);
      setConnection(result);
      setSuccessMessage('Settings saved successfully.');
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(`Failed to save: ${error.message}`);
      } else {
        setErrorMessage('Failed to save settings.');
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTestConnection() {
    setSuccessMessage('');
    setErrorMessage('');
    setIsTesting(true);
    try {
      const result = await testIbkrConnection();
      if (result.success) {
        setSuccessMessage(result.message);
        await fetchConnection();
        // Refresh the live badge so it reflects the latest observed result.
        refresh();
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(`Test failed: ${error.message}`);
      } else {
        setErrorMessage('Failed to test connection.');
      }
    } finally {
      setIsTesting(false);
    }
  }

  async function handleDelete() {
    setSuccessMessage('');
    setErrorMessage('');
    setIsDeleting(true);
    try {
      await deleteIbkrConnection();
      setConnection(null);
      setGatewayUrl('');
      setEnabled(false);
      setShowDeleteConfirm(false);
      setSuccessMessage('Connection settings deleted.');
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(`Failed to delete: ${error.message}`);
      }
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <div>
        <h2 id="ibkr-settings-heading" style={{ marginBottom: '0.5rem' }}>IBKR Market Data</h2>
        <p style={{ color: 'var(--color-muted)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <section aria-labelledby="ibkr-settings-heading">
      <h2 id="ibkr-settings-heading" style={{ marginBottom: '0.5rem' }}>IBKR Market Data</h2>
      <p style={{ color: 'var(--color-muted)', marginBottom: '1rem' }}>
        Connect to your locally-running IBKR Client Portal Gateway via ngrok for automated price updates.
      </p>

      {/* Live connection status badge — driven by useIbkrLiveStatus, independent of the enabled toggle. */}
      <div
        role="status"
        aria-live="polite"
        style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
      >
        <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>Status:</span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            fontSize: '0.8rem',
            fontWeight: 600,
            backgroundColor: 'var(--color-surface-2)',
            color: STATUS_COLORS[status],
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: STATUS_COLORS[status] }} />
          {STATUS_LABELS[status]}
        </span>
        {checkedAt && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
            last checked {new Date(checkedAt).toLocaleTimeString()}
          </span>
        )}
        <button
          type="button"
          aria-label="Refresh IBKR connection status"
          onClick={refresh}
          disabled={isChecking}
          style={{
            padding: '0.25rem 0.75rem',
            backgroundColor: 'transparent',
            color: 'var(--color-brand)',
            border: '1px solid var(--color-brand)',
            borderRadius: '4px',
            cursor: isChecking ? 'not-allowed' : 'pointer',
            fontSize: '0.75rem',
            opacity: isChecking ? 0.7 : 1,
          }}
        >
          {isChecking ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      {/* Explanatory message when the connection is not healthy. */}
      {message && status !== ConnectionStatus.CONNECTED && status !== 'CHECKING' && (
        <p
          role="status"
          style={{
            marginTop: '-0.5rem',
            marginBottom: '1rem',
            fontSize: '0.8rem',
            color: status === ConnectionStatus.AUTH_FAILED ? 'var(--color-chip-danger-text)' : 'var(--color-muted)',
            maxWidth: '520px',
            lineHeight: 1.4,
          }}
        >
          {message}
        </p>
      )}

      {/* Form */}
      <div style={{ display: 'grid', gap: '1rem', maxWidth: '500px' }}>
        <div>
          <label htmlFor="ibkr-gateway-url" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
            Gateway URL
          </label>
          <input
            id="ibkr-gateway-url"
            type="url"
            value={gatewayUrl}
            onChange={(e) => { setGatewayUrl(e.target.value); setSuccessMessage(''); setErrorMessage(''); }}
            disabled={isSaving}
            placeholder="https://your-subdomain.ngrok-free.app"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid var(--color-input-border)',
              borderRadius: '4px',
              fontSize: '1rem',
              minHeight: '44px',
              boxSizing: 'border-box',
            }}
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '4px' }}>
            Your ngrok URL pointing to the local IBKR Client Portal Gateway.
          </p>
        </div>

        {/* Enable automated fetching toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label htmlFor="ibkr-enabled" style={{ fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' }}>
            Enable automated data fetching (every 5 min during market hours)
          </label>
          <button
            id="ibkr-enabled"
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => { setEnabled(!enabled); setSuccessMessage(''); setErrorMessage(''); }}
            disabled={isSaving}
            style={{
              width: '48px',
              height: '26px',
              minHeight: '26px',
              maxHeight: '26px',
              borderRadius: '13px',
              border: 'none',
              padding: 0,
              backgroundColor: enabled ? 'var(--color-brand)' : 'var(--color-input-border)',
              position: 'relative',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              flexShrink: 0,
            }}
          >
            <span style={{ position: 'absolute', top: '3px', left: enabled ? '25px' : '3px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'var(--color-surface)', transition: 'left 0.2s' }} />
          </button>
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '-0.5rem' }}>
          Automated data fetching is independent of the live connection status shown above; toggling it does not change the connection badge.
        </p>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
        <button
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: isDirty ? 'var(--color-brand)' : 'var(--color-brand-disabled)',
            color: 'var(--color-on-brand)',
            border: 'none',
            borderRadius: '4px',
            cursor: (isSaving || !isDirty) ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            minHeight: '44px',
            opacity: isSaving ? 0.7 : 1,
            transition: 'background-color 0.2s',
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>

        {connection && (
          <button
            onClick={handleTestConnection}
            disabled={isTesting || isSaving}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'transparent',
              color: 'var(--color-brand)',
              border: '1px solid var(--color-brand)',
              borderRadius: '4px',
              cursor: isTesting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              minHeight: '44px',
              opacity: isTesting ? 0.7 : 1,
            }}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </button>
        )}

        {connection && !showDeleteConfirm && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSaving || isDeleting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'transparent',
              color: 'var(--color-danger)',
              border: '1px solid var(--color-danger)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              minHeight: '44px',
            }}
          >
            Delete
          </button>
        )}

        {showDeleteConfirm && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-danger)' }}>Are you sure?</span>
            <button onClick={handleDelete} disabled={isDeleting} style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', minHeight: '44px' }}>
              {isDeleting ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button onClick={() => setShowDeleteConfirm(false)} style={{ padding: '0.5rem 1rem', backgroundColor: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-muted)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', minHeight: '44px' }}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      {successMessage && <p role="status" style={{ marginTop: '1rem', color: 'var(--color-success)', fontWeight: 500 }}>{successMessage}</p>}
      {errorMessage && <p role="alert" style={{ marginTop: '1rem', color: 'var(--color-danger)', fontWeight: 500 }}>{errorMessage}</p>}
    </section>
  );
}
